import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import slugify from "slugify";

import { getById, getCoverImageByExperienceId, getImagesByExperienceId, getItineraryItemImage } from "../../../api/farm-service/experienceService";
import { getFarmById } from "../../../api/farm-service/farmService";
import SectionSeparator from "../../../components/common/SectionSeparator";
import Logger from "../../../utils/logger";

import ExperienceGallery from "./sections/ExperienceGallery";
import ExperienceTitleSection from "./sections/ExperienceTitleSection";
import ExperienceItinerary from "./sections/ExperienceItinerary";
import ExperienceMap from "./sections/ExperienceMap";
import ExperienceFarmCard from "./sections/ExperienceFarmCard";
import ExperienceSidebar from "./sections/ExperienceSidebar";

import ExpStyle from "../../../assets/stylesheets/experience/ExperienceDetailPage.module.css";

const log = Logger.create("ExperienceDetailPage");

const SEPARATOR_VARIANT = "thick";
const SEPARATOR_SPACING = "none";
const SEPARATOR_PADDING = "medium";

// ── Mock data (set to true to test UI without backend) ────────────────────────
const USE_MOCK_DATA = false;

const MOCK_EXPERIENCE = {
  id: 1,
  title: "Apple Picking & Cider Tasting",
  description:
    "Join us for a hands-on apple picking adventure through our historic orchard. You'll learn about different apple varieties, pick your own bag of fresh apples, and finish with a guided cider tasting featuring our farm-pressed blends. Perfect for families, couples, and anyone who loves the outdoors.",
  category: "harvest",
  duration: "2.5 hours",
  groupSize: 10,
  price: 45,
  farmId: 1,
  coverImage: {
    viewUrl: "https://images.unsplash.com/photo-1570913149827-d2ac84ab3f9a?w=800&h=600&fit=crop",
  },
  galleryImages: [
    { viewUrl: "https://images.unsplash.com/photo-1568702846914-96b305d2aaeb?w=400&h=300&fit=crop" },
    { viewUrl: "https://images.unsplash.com/photo-1506617420156-8e4536971650?w=400&h=300&fit=crop" },
    { viewUrl: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400&h=300&fit=crop" },
    { viewUrl: "https://images.unsplash.com/photo-1559181567-c3190bfbf396?w=400&h=300&fit=crop" },
  ],
  itineraryItems: [
    {
      id: 1,
      title: "Welcome & Orchard Introduction",
      description: "Meet your guide and get a brief history of Sunny Ridge Orchard. Learn about sustainable apple farming and what makes our varieties special.",
      duration: "15 min",
      image: { viewUrl: "https://images.unsplash.com/photo-1570913149827-d2ac84ab3f9a?w=200&h=200&fit=crop" },
    },
    {
      id: 2,
      title: "Guided Orchard Walk",
      description: "Walk through rows of heritage apple trees with your guide. Discover the differences between Honeycrisp, Gala, McIntosh and more.",
      duration: "45 min",
      image: { viewUrl: "https://images.unsplash.com/photo-1568702846914-96b305d2aaeb?w=200&h=200&fit=crop" },
    },
    {
      id: 3,
      title: "Apple Picking",
      description: "Fill your own bag with freshly picked apples to take home. Our staff will show you how to identify ripe fruit.",
      duration: "30 min",
      image: { viewUrl: "https://images.unsplash.com/photo-1506617420156-8e4536971650?w=200&h=200&fit=crop" },
    },
    {
      id: 4,
      title: "Farm-Pressed Cider Tasting",
      description: "Sit down in our heritage barn for a guided tasting of four signature ciders — from sweet to dry — paired with local cheese.",
      duration: "30 min",
      image: { viewUrl: "https://images.unsplash.com/photo-1559181567-c3190bfbf396?w=200&h=200&fit=crop" },
    },
  ],
  availableDates: [
    "2026-09-12",
    "2026-09-19",
    "2026-09-26",
    "2026-10-03",
    "2026-10-10",
  ],
  location: {
    id: 1,
    latitude: "43.7315",
    longitude: "-79.7624",
  },
};

const MOCK_FARM = {
  id: 1,
  name: "Sunny Ridge Orchard",
  description: "A family-owned orchard nestled in the hills of Ontario.",
  webUrl: "https://www.sunnyridgeorchard.ca",
  coverImage: {
    viewUrl: "https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=600&h=400&fit=crop",
  },
  location: {
    address: { city: "Caledon", state: "Ontario" },
    latitude: "43.7315",
    longitude: "-79.7624",
  },
  banner: "Popular",
};

// ── Sample experiences (shown when no backend data exists) ────────────────────
const SAMPLE_EXPERIENCES = {
  "sample-1": {
    experience: {
      id: "sample-1",
      title: "Honey & Beekeeping Discovery",
      description:
        "Suit up and step into the world of bees! Our experienced apiarist will guide you through the hive, explaining how honey is made, how colonies communicate, and how we practice sustainable beekeeping. You'll extract honey by hand and take home your own jar of fresh clover honey. No prior experience needed.",
      category: "workshops",
      duration: "2 hours",
      groupSize: 8,
      price: 65,
      startAt: "2026-07-12T10:00:00Z",
      endAt: "2026-07-12T12:00:00Z",
      farmId: "sample-farm-1",
      coverImage: { viewUrl: "https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=800&h=600&fit=crop" },
      galleryImages: [
        { viewUrl: "https://images.unsplash.com/photo-1587049352851-8d4e89133924?w=400&h=300&fit=crop" },
        { viewUrl: "https://images.unsplash.com/photo-1601055903351-68c911b2e4ea?w=400&h=300&fit=crop" },
        { viewUrl: "https://images.unsplash.com/photo-1590856029826-c7a73142bbf1?w=400&h=300&fit=crop" },
        { viewUrl: "https://images.unsplash.com/photo-1555685812-4b943f1cb0eb?w=400&h=300&fit=crop" },
      ],
      itineraryItems: [
        { id: "s1-1", title: "Welcome & Safety Briefing", description: "Meet your apiarist, suit up in protective gear, and learn basic bee safety before entering the apiary.", duration: "15 min", image: { viewUrl: "https://images.unsplash.com/photo-1587049352851-8d4e89133924?w=200&h=200&fit=crop" } },
        { id: "s1-2", title: "Live Hive Inspection", description: "Open a real hive with your guide. Spot the queen, identify worker and drone bees, and observe the honeycomb up close.", duration: "45 min", image: { viewUrl: "https://images.unsplash.com/photo-1601055903351-68c911b2e4ea?w=200&h=200&fit=crop" } },
        { id: "s1-3", title: "Honey Extraction by Hand", description: "Use a manual extractor to spin fresh frames and watch golden honey pour out. Taste it straight from the extractor!", duration: "30 min", image: { viewUrl: "https://images.unsplash.com/photo-1590856029826-c7a73142bbf1?w=200&h=200&fit=crop" } },
        { id: "s1-4", title: "Jar Your Own Honey", description: "Fill, seal, and label your own jar of clover honey to take home as a sweet souvenir.", duration: "15 min", image: { viewUrl: "https://images.unsplash.com/photo-1555685812-4b943f1cb0eb?w=200&h=200&fit=crop" } },
      ],
      location: { latitude: "44.1532", longitude: "-79.8647" },
    },
    farm: {
      id: "sample-farm-1",
      name: "Clover Hill Apiary",
      description: "A fourth-generation family apiary in the rolling hills of Simcoe County, Ontario. We manage over 80 hives using natural, treatment-free methods.",
      coverImage: { viewUrl: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=400&fit=crop" },
      location: { address: { city: "Barrie", state: "Ontario" }, latitude: "44.1532", longitude: "-79.8647" },
    },
  },
  "sample-2": {
    experience: {
      id: "sample-2",
      title: "Lavender Harvest & Wreath Making",
      description:
        "Spend a morning in our fragrant lavender fields during peak bloom. You'll harvest your own bundles of fresh lavender, then sit down in our barn workshop to craft a beautiful dried wreath to take home. Includes herbal tea and lavender shortbread. Perfect for a relaxing summer outing.",
      category: "seasonal",
      duration: "3 hours",
      groupSize: 12,
      price: 55,
      startAt: "2026-07-05T09:30:00Z",
      endAt: "2026-07-05T12:30:00Z",
      farmId: "sample-farm-2",
      coverImage: { viewUrl: "https://images.unsplash.com/photo-1499002238440-d264edd596ec?w=800&h=600&fit=crop" },
      galleryImages: [
        { viewUrl: "https://images.unsplash.com/photo-1468581264429-2548ef9eb732?w=400&h=300&fit=crop" },
        { viewUrl: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=300&fit=crop" },
        { viewUrl: "https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=400&h=300&fit=crop" },
        { viewUrl: "https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=400&h=300&fit=crop" },
      ],
      itineraryItems: [
        { id: "s2-1", title: "Welcome Walk Through the Fields", description: "Stroll through 3 acres of blooming lavender while your host shares the story of the farm and the lavender varieties grown here.", duration: "20 min", image: { viewUrl: "https://images.unsplash.com/photo-1468581264429-2548ef9eb732?w=200&h=200&fit=crop" } },
        { id: "s2-2", title: "Guided Harvesting", description: "Learn the correct technique for cutting lavender at peak bloom. You'll harvest enough to make your wreath and take a generous bundle home.", duration: "40 min", image: { viewUrl: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=200&h=200&fit=crop" } },
        { id: "s2-3", title: "Wreath-Making Workshop", description: "Sit down at our communal barn tables and follow along with step-by-step wreath-making instructions. All materials provided.", duration: "60 min", image: { viewUrl: "https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=200&h=200&fit=crop" } },
        { id: "s2-4", title: "Tea & Tasting", description: "Wind down with lavender herbal tea and farm-baked lavender shortbread in the garden patio.", duration: "20 min", image: { viewUrl: "https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=200&h=200&fit=crop" } },
      ],
      location: { latitude: "43.9200", longitude: "-79.3800" },
    },
    farm: {
      id: "sample-farm-2",
      name: "Purple Meadow Farm",
      description: "Ontario's largest lavender farm, open every summer for U-pick, workshops, and farm-crafted lavender products.",
      coverImage: { viewUrl: "https://images.unsplash.com/photo-1499002238440-d264edd596ec?w=600&h=400&fit=crop" },
      location: { address: { city: "Newmarket", state: "Ontario" }, latitude: "43.9200", longitude: "-79.3800" },
    },
  },
};

export default function ExperienceDetailPage() {
  const { experienceId } = useParams();
  const navigate = useNavigate();

  const [experience, setExperience] = useState(null);
  const [farm, setFarm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 1) Load experience
  useEffect(() => {
    if (!experienceId) return;

    if (USE_MOCK_DATA) {
      setExperience(MOCK_EXPERIENCE);
      setLoading(false);
      return;
    }

    // Sample experiences — served locally without API
    if (SAMPLE_EXPERIENCES[experienceId]) {
      setExperience(SAMPLE_EXPERIENCES[experienceId].experience);
      setLoading(false);
      return;
    }

    setLoading(true);
    getById(experienceId)
      .then(async (res) => {
        const d1 = res?.data ?? res;
        const exp = d1?.data ?? d1;

        // 1) Cover image
        let coverImage = exp.coverImage ?? null;
        if (!coverImage && exp.coverImageId) {
          try {
            const r = await getCoverImageByExperienceId(exp.id ?? experienceId);
            const viewUrl = r?.data?.data?.viewUrl ?? r?.data?.viewUrl ?? null;
            if (viewUrl) coverImage = { viewUrl };
          } catch (e) {
            log.warn("Failed to load cover image:", e);
          }
        }

        // 2) Gallery images
        let galleryImages = exp.galleryImages ?? [];
        if (galleryImages.length === 0) {
          try {
            const r = await getImagesByExperienceId(exp.id ?? experienceId);
            // Response: { data: { data: [...] } } or { data: [...] }
            const imgList = r?.data?.data ?? r?.data ?? [];
            galleryImages = Array.isArray(imgList) ? imgList : [];
          } catch (e) {
            log.warn("Failed to load gallery images:", e);
          }
        }

        // 3) Itinerary item images
        const rawItinerary = exp.itineraryItems ?? exp.itinerary ?? [];
        const itineraryWithImages = await Promise.allSettled(
          rawItinerary.map(async (item) => {
            if (item.image?.viewUrl || item.imageUrl) return item;
            if (!item.id) return item;
            try {
              const r = await getItineraryItemImage(exp.id ?? experienceId, item.id);
              const viewUrl = r?.data?.data?.viewUrl ?? r?.data?.viewUrl ?? null;
              return viewUrl ? { ...item, image: { viewUrl } } : item;
            } catch (e) {
              if (e?.response?.status !== 404) {
                log.warn(`[itinerary] item ${item.id} image fetch failed:`, e?.response?.status);
              }
              return item;
            }
          })
        ).then((results) => results.map((r) => (r.status === "fulfilled" ? r.value : r.reason)));

        setExperience({
          ...exp,
          coverImage,
          galleryImages,
          itineraryItems: itineraryWithImages,
        });
        setError(null);
      })
      .catch((err) => {
        log.error("Failed to load experience:", err);
        setError("Failed to load experience.");
      })
      .finally(() => setLoading(false));
  }, [experienceId]);

  // 2) Load associated farm
  useEffect(() => {
    if (USE_MOCK_DATA) {
      setFarm(MOCK_FARM);
      return;
    }

    // Sample experience — use bundled farm data
    if (SAMPLE_EXPERIENCES[experienceId]) {
      setFarm(SAMPLE_EXPERIENCES[experienceId].farm);
      return;
    }

    const farmId = experience?.farmId ?? experience?.farm?.id;
    if (!farmId) return;
    getFarmById(farmId)
      .then((res) => {
        const d1 = res?.data ?? res;
        setFarm(d1?.data ?? d1);
      })
      .catch((err) => log.error("Failed to load farm:", err));
  }, [experience, experienceId]);

  if (loading) return <main className={ExpStyle["exp-main"]} style={{ minHeight: "100vh" }} />;
  if (error) return <div style={{ padding: "2rem", textAlign: "center", color: "red" }}>{error}</div>;
  if (!experience) return <div style={{ padding: "2rem", textAlign: "center" }}>Experience not found.</div>;

  const farmLocation = experience?.location || farm?.location || null;
  const farmWebUrl = farm?.webUrl || null;
  const itinerary = experience?.itineraryItems || experience?.itinerary || [];

  const isSampleFarm = String(farm?.id ?? "").startsWith("sample-");

  const handleFarmClick = () => {
    if (!farm || isSampleFarm) return;
    const slug = slugify(farm.name || "", { lower: true, strict: true });
    navigate(`/farm/${farm.id}/${slug}`);
  };

  return (
    <main className={ExpStyle["exp-main"]}>
      <section className={ExpStyle["exp-overview"]}>
        <ExperienceGallery experience={experience} />

        <div className={ExpStyle["exp-container"]}>
          {/* Left column */}
          <div className={ExpStyle["exp-main-col"]}>
            <ExperienceTitleSection experience={experience} farmName={farm?.name} />

            <SectionSeparator variant={SEPARATOR_VARIANT} spacing={SEPARATOR_SPACING} padding={SEPARATOR_PADDING} />

            <ExperienceItinerary itinerary={itinerary} />

            <SectionSeparator variant={SEPARATOR_VARIANT} spacing={SEPARATOR_SPACING} padding={SEPARATOR_PADDING} />
          </div>

          {/* Desktop sidebar + mobile sticky bar + mobile dates — all in one component */}
          <ExperienceSidebar experience={experience} farmWebUrl={farmWebUrl} farmId={farm?.id} farmName={farm?.name} />
        </div>
      </section>

      {farmLocation && (
        <>
          <div className={ExpStyle["separator-container"]}>
            <SectionSeparator variant={SEPARATOR_VARIANT} spacing={SEPARATOR_SPACING} padding={SEPARATOR_PADDING} />
          </div>
          <section>
            <ExperienceMap location={farmLocation} />
          </section>
        </>
      )}

      {farm && (
        <>
          <div className={ExpStyle["separator-container"]}>
            <SectionSeparator variant={SEPARATOR_VARIANT} spacing={SEPARATOR_SPACING} padding={SEPARATOR_PADDING} />
          </div>
          <section>
            <ExperienceFarmCard farm={farm} onFarmClick={handleFarmClick} />
          </section>
        </>
      )}
    </main>
  );
}
